import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge, Button, LoadingOrbit, TagBadge, Text, TextField, cn } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { usePrompts, type PromptSummary, type PromptTag } from '../api';

type Props = {
  /** The currently open prompt id (highlighted row), or undefined for none. */
  selectedId: string | undefined;
  onNew: () => void;
};

/** Stable key for a tag (org tags have ids; global tags key on their name). */
const tagKey = (t: PromptTag) => t.id ?? `name:${t.name}`;

/**
 * Left rail of the Prompt Library workspace: a searchable, tag-filterable list
 * of prompts. Selecting a row deep-links to `/prompt-library/$promptId`.
 */
export function PromptRail({ selectedId, onNew }: Props) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePrompts();
  const prompts = data?.data ?? [];

  const [search, setSearch] = React.useState('');
  // Filter by tag name (names unify org + global tags in the rail).
  const [activeTagNames, setActiveTagNames] = React.useState<Set<string>>(new Set());

  // The distinct tags across all prompts, for the filter chips.
  const allTags = React.useMemo(() => {
    const byName = new Map<string, PromptTag>();
    for (const p of prompts) {
      for (const t of p.tags) if (!byName.has(t.name)) byName.set(t.name, t);
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [prompts]);

  const toggleTag = (name: string) => {
    setActiveTagNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return prompts.filter((p) => {
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false);
      const matchesTags =
        activeTagNames.size === 0 || p.tags.some((t) => activeTagNames.has(t.name));
      return matchesSearch && matchesTags;
    });
  }, [prompts, search, activeTagNames]);

  const selectPrompt = (p: PromptSummary) => {
    void navigate({ to: '/prompt-library/$promptId', params: { promptId: p.id } });
  };

  return (
    <div className="flex w-[340px] shrink-0 flex-col border-r border-border bg-card">
      <div className="shrink-0 border-b border-border px-4 py-4">
        <div className="flex items-center justify-between">
          <Text size="sm" weight="semibold" className="text-foreground">
            Prompt Library
          </Text>
          <Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onPress={onNew}>
            New
          </Button>
        </div>
        <TextField
          aria-label="Search prompts"
          value={search}
          onChange={setSearch}
          placeholder="Search prompts…"
          className="mt-3"
        />
        {allTags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {allTags.map((t) => {
              const active = activeTagNames.has(t.name);
              return (
                <button
                  key={tagKey(t)}
                  type="button"
                  onClick={() => toggleTag(t.name)}
                  className={cn(
                    'rounded-full transition-opacity',
                    active ? 'opacity-100 ring-2 ring-primary ring-offset-1' : 'opacity-70 hover:opacity-100'
                  )}
                  title={active ? `Clear ${t.name} filter` : `Filter by ${t.name}`}
                >
                  <TagBadge name={t.name} color={t.color} size="sm" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingOrbit size="md" />
          </div>
        ) : isError ? (
          <Text size="sm" className="block px-4 py-6 text-muted-foreground">
            Couldn't load prompts. Try refreshing.
          </Text>
        ) : filtered.length === 0 ? (
          <Text size="sm" className="block px-4 py-6 text-muted-foreground">
            {prompts.length === 0 ? 'No prompts yet.' : 'No prompts match your filters.'}
          </Text>
        ) : (
          <ul>
            {filtered.map((p) => {
              const active = p.id === selectedId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => selectPrompt(p)}
                    className={cn(
                      'w-full border-b border-border px-4 py-3 text-left transition-colors',
                      active ? 'bg-accent' : 'hover:bg-muted/50'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Text
                        size="sm"
                        weight="medium"
                        className="min-w-0 flex-1 truncate text-foreground"
                      >
                        {p.title}
                      </Text>
                      {p.scope === 'global' && (
                        <Badge variant="secondary" className="shrink-0">
                          Cofactor
                        </Badge>
                      )}
                    </div>
                    {p.description && (
                      <Text size="xs" className="mt-0.5 line-clamp-2 block text-muted-foreground">
                        {p.description}
                      </Text>
                    )}
                    {p.tags.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {p.tags.map((t) => (
                          <TagBadge key={tagKey(t)} name={t.name} color={t.color} size="sm" />
                        ))}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
